import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react"
import {
  useAuthBroadcastChannel,
  useAuthVerification,
  useAutoVerification,
  useProfileManagement,
  useTokenValidation,
  useGetAvacusProfile,
} from "@/hooks/auth"

import { useWalletConnection } from "@/hooks/use-wallet-connection"
import { cookieStorage, useConnection } from "wagmi"
import { CookieKey } from "@/configs/common"
import { unregisterCurrentPushDevice } from "@/lib/push-devices"

interface AuthProviderContext {
  accessToken?: string
  isVerified: boolean
  isAuthLoading: boolean
  address?: `0x${string}`
  displayName?: string
  isFetchingUser: boolean
  isProfileError?: boolean
  user?: any
  isSigning: boolean
  isSigningInOtherTab: boolean

  updateAccessToken: (token?: string) => void
  connectWallet: (callback?: () => void) => void
  disconnectWallet: () => Promise<void>
  refetchProfile: () => Promise<void>
  verifyAccount: () => Promise<void>
}

const initialValue: AuthProviderContext = {
  accessToken: undefined,
  isVerified: false,
  isAuthLoading: false,
  isFetchingUser: false,
  isSigning: false,
  isSigningInOtherTab: false,
  updateAccessToken: () => {},
  connectWallet: () => {},
  disconnectWallet: async () => {},
  refetchProfile: async () => {},
  verifyAccount: async () => {},
}

export const AuthContext = createContext<AuthProviderContext>(initialValue)

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isVerified, setIsVerified] = useState<boolean>(false)

  const {
    data: user,
    isFetching: isFetchingUser,
    isError: isProfileError,
    error: profileError,
    address,
    displayName,
    refetch: refetchProfile,
  } = useGetAvacusProfile()

  // Token validation hook
  const { accessToken, updateAccessToken } = useTokenValidation({
    setIsVerified,
  })

  // Wallet connection hook
  const {
    connectWallet: connectWalletBase,
    disconnectWallet: disconnectWalletBase,
    isAuthLoading,
    isManualConnect,
  } = useWalletConnection({
    setIsVerified,
  })

  // Wrap connect/disconnect to pass updateAccessToken
  const connectWallet = useCallback(
    (callback?: () => void) => {
      connectWalletBase(callback, updateAccessToken)
    },
    [connectWalletBase, updateAccessToken]
  )

  const disconnectWallet = useCallback(async () => {
    await unregisterCurrentPushDevice(address)
    return disconnectWalletBase(updateAccessToken)
  }, [address, disconnectWalletBase, updateAccessToken])

  // Auth verification hook
  const { verifyAccount: verifyAccountBase, nonce } = useAuthVerification({
    isVerified,
    setIsVerified,
    updateAccessToken,
    accessToken,
  })

  const { isConnected } = useConnection()

  const onVerifiedFromOtherTab = useCallback(() => {
    const token = cookieStorage.getItem(CookieKey.AuthToken) ?? undefined
    updateAccessToken(token)
    refetchProfile()
  }, [updateAccessToken, refetchProfile])

  const {
    wrapVerifyAccount,
    postAutoSignStarted,
    hasAutoSignTriggered,
    isSigning,
    isSigningInOtherTab,
  } = useAuthBroadcastChannel({
    isConnected: !!isConnected,
    onVerified: onVerifiedFromOtherTab,
  })

  const verifyAccount = useCallback(
    () => wrapVerifyAccount(verifyAccountBase)(),
    [wrapVerifyAccount, verifyAccountBase]
  )

  // Auto-verification hook (only one tab auto-triggers; others skip via hasAutoSignTriggered)
  useAutoVerification({
    isVerified,
    isAuthLoading,
    isFetchingUser,
    isManualConnect,
    verifyAccount,
    postAutoSignStarted,
    hasAutoSignTriggered,
    nonce,
    user,
    isProfileError,
  })

  // Profile management hook (no modal; Auth page shows inactive UI)
  const { handleRefetchProfile } = useProfileManagement({
    user,
    address,
    profileError,
    refetchProfile,
    disconnectWallet,
    setIsVerified,
  })

  const contextValue: AuthProviderContext = {
    accessToken,
    user,
    isVerified,
    isSigning,
    isSigningInOtherTab,
    updateAccessToken,
    connectWallet,
    disconnectWallet,
    refetchProfile: handleRefetchProfile,
    isAuthLoading,
    verifyAccount,
    isFetchingUser,
    isProfileError,
    address,
    displayName,
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export const useAuth = () => {
  return useContext(AuthContext)
}
